/**
 * BR OS > Tax Reserves > Overview, top block in the Property Portfolio
 * layout (Fan, 2026-10-27): country pills, year switcher, GBP / EUR,
 * KPI cards, a monthly reserves chart, a monthly table and a by-country
 * table. Figures: GET /bros/tax-reserves/country-months (all countries);
 * converted with /fx/rates, labelled approx. Read-only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { CurrencyToggle } from '../CurrencyToggle';
import { KpiCard, YearNav, PillTabs, SectionCard, MonthlyStackedChart, fmtMoney } from './TaxDashboardParts';
import { brosTaxReservesAPI, exchangeRatesAPI } from '../../lib/api';
import { usePortfolioPrefsStore } from '../../store';
import { fxFromRates, makeDisplay } from '../../lib/taxOverview';
import { buildBrosOverview, countryOptions, countryLabel } from '../../lib/brosTaxOverview';
import { cn } from '../../lib/utils';

const SERIES = [
  { key: 'vat_payable', label: 'VAT reserve', color: '#3B82F6' },
  { key: 'tax', label: 'Corporation tax reserve', color: '#8B5CF6' },
];

function pct(a, b) {
  return b ? `${((a / b) * 100).toFixed(1)}%` : '-';
}

export function BROSTaxOverview() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [country, setCountry] = useState('all');
  const dc = usePortfolioPrefsStore((s) => s.displayCurrency) || 'GBP';
  const [cm, setCm] = useState(null);
  const [fx, setFx] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      Promise.resolve().then(() => brosTaxReservesAPI.getCountryMonths({ countries: 'all', year })),
      Promise.resolve().then(() => exchangeRatesAPI.getLatest()).catch(() => ({ data: null })),
    ]).then(([a, b]) => {
      if (!alive) return;
      setCm(a?.data || null);
      setFx(fxFromRates(b?.data));
      setError(null);
    }).catch((e) => { if (alive) setError(e?.message || 'Could not load the reserves'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [year]);

  useEffect(() => load(), [load]);

  const view = useMemo(() => buildBrosOverview({ countryMonths: cm, fx, country, year }), [cm, fx, country, year]);
  const options = useMemo(() => countryOptions(cm, fx), [cm, fx]);
  const show = useMemo(() => makeDisplay(fx, dc), [fx, dc]);
  const money = (gbp) => fmtMoney(show(gbp), dc);
  const k = view.kpis;
  const nowMonth = new Date().toISOString().slice(0, 7);
  const chartData = view.months.map((m) => ({ label: m.label, vat_payable: show(m.vat_payable) || 0, tax: show(m.tax) || 0 }));

  return (
    <div className="flex flex-col gap-6" data-testid="bros-tax-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PillTabs value={country} onChange={setCountry} options={options} testId="bros-tax-country" />
        <div className="flex items-center gap-2">
          <CurrencyToggle />
          <YearNav year={year} setYear={setYear} />
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></CardContent></Card>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bros-tax-kpis">
            <KpiCard label="Cash received" value={money(k.cash_received)} subDetail={`${k.payments} payments`} tooltip="Client payments received, by bank payment date" />
            <KpiCard label="Supplier cost" value={money(k.supplier_cost)} subDetail={pct(k.supplier_cost, k.cash_received) + ' of cash received'} />
            <KpiCard label="VAT reserve" value={money(k.vat_payable)} subDetail="VAT payable: output less input VAT" />
            <KpiCard label="Corporation tax reserve" value={money(k.tax)} subDetail="On profit after supplier cost" />
            <KpiCard label="Profit after tax" value={money(k.profit_after_tax)} subDetail={pct(k.profit_after_tax, k.cash_received) + ' margin'} />
            <KpiCard label="Profit share" value={money(k.profit_share)} subDetail={`${cm?.split_pct ?? 50}% of profit after tax`} />
            <KpiCard label="Working capital" value={money(k.working_capital)} subDetail="The rest of profit after tax" />
            <KpiCard
              label="Supplier cost unknown"
              value={String(k.unresolved)}
              tone={k.unresolved > 0 ? 'bad' : undefined}
              subDetail="Payments left out of profit until the cost is known"
            />
          </div>

          <SectionCard title={`Monthly reserves (${dc} approx)`} subtitle="VAT and corporation tax set aside from each month's client payments." testId="bros-tax-monthly-chart">
            <MonthlyStackedChart data={chartData} series={SERIES} ccy={dc} />
          </SectionCard>

          <SectionCard title={`Monthly reserves (${dc} approx)`} testId="bros-tax-monthly-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Cash received</TableHead>
                  <TableHead className="text-right">Supplier cost</TableHead>
                  <TableHead className="text-right">VAT reserve</TableHead>
                  <TableHead className="text-right">Corp. tax reserve</TableHead>
                  <TableHead className="text-right">Profit after tax</TableHead>
                  <TableHead className="text-right">Profit share</TableHead>
                  <TableHead className="text-right">Working capital</TableHead>
                  <TableHead className="text-right">Margin %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {view.months.map((m) => (
                  <TableRow key={m.month} className={cn(m.month === nowMonth && 'bg-emerald-50/60 dark:bg-emerald-950/20')}>
                    <TableCell className="font-medium">
                      {m.label}
                      {m.month === nowMonth && <Badge variant="outline" className="ml-2 text-[10px]">Now</Badge>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{m.cash_received ? money(m.cash_received) : '-'}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{m.supplier_cost ? money(m.supplier_cost) : '-'}</TableCell>
                    <TableCell className="text-right font-mono">{m.vat_payable ? money(m.vat_payable) : '-'}</TableCell>
                    <TableCell className="text-right font-mono">{m.tax ? money(m.tax) : '-'}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{m.profit_after_tax ? money(m.profit_after_tax) : '-'}</TableCell>
                    <TableCell className="text-right font-mono">{m.profit_share ? money(m.profit_share) : '-'}</TableCell>
                    <TableCell className="text-right font-mono">{m.working_capital ? money(m.working_capital) : '-'}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{m.cash_received ? pct(m.profit_after_tax, m.cash_received) : '-'}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold border-t-2">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-mono text-emerald-700">{money(view.totalsRow.cash_received)}</TableCell>
                  <TableCell className="text-right font-mono text-red-600">{money(view.totalsRow.supplier_cost)}</TableCell>
                  <TableCell className="text-right font-mono">{money(view.totalsRow.vat_payable)}</TableCell>
                  <TableCell className="text-right font-mono">{money(view.totalsRow.tax)}</TableCell>
                  <TableCell className="text-right font-mono">{money(view.totalsRow.profit_after_tax)}</TableCell>
                  <TableCell className="text-right font-mono">{money(view.totalsRow.profit_share)}</TableCell>
                  <TableCell className="text-right font-mono">{money(view.totalsRow.working_capital)}</TableCell>
                  <TableCell className="text-right font-mono">{pct(view.totalsRow.profit_after_tax, view.totalsRow.cash_received)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </SectionCard>

          <SectionCard title="By country" subtitle="In each country's own currency." testId="bros-tax-by-country">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Country</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">Cash received</TableHead>
                  <TableHead className="text-right">Supplier cost</TableHead>
                  <TableHead className="text-right">VAT reserve</TableHead>
                  <TableHead className="text-right">Corp. tax reserve</TableHead>
                  <TableHead className="text-right">Profit after tax</TableHead>
                  <TableHead className="text-right">Margin %</TableHead>
                  <TableHead className="text-right">Payments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {view.countries.map((c) => (
                  <TableRow key={`${c.country}-${c.currency}`}>
                    <TableCell className="font-medium">{countryLabel(c.country)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{c.currency}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{fmtMoney(c.native.cash_received, c.currency)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{fmtMoney(c.native.supplier_cost, c.currency)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(c.native.vat_payable, c.currency)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(c.native.tax, c.currency)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmtMoney(c.native.profit_after_tax, c.currency)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{pct(c.native.profit_after_tax, c.native.cash_received)}</TableCell>
                    <TableCell className="text-right">
                      {c.native.payments}
                      {c.native.unresolved > 0 && <span className="text-red-600 text-xs ml-1">({c.native.unresolved} cost unknown)</span>}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold border-t-2">
                  <TableCell colSpan={2}>{`Total (${dc} approx)`}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-700">{money(k.cash_received)}</TableCell>
                  <TableCell className="text-right font-mono text-red-600">{money(k.supplier_cost)}</TableCell>
                  <TableCell className="text-right font-mono">{money(k.vat_payable)}</TableCell>
                  <TableCell className="text-right font-mono">{money(k.tax)}</TableCell>
                  <TableCell className="text-right font-mono">{money(k.profit_after_tax)}</TableCell>
                  <TableCell className="text-right font-mono">{pct(k.profit_after_tax, k.cash_received)}</TableCell>
                  <TableCell className="text-right">{k.payments}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </SectionCard>

          {view.unconverted > 0 && (
            <p className="text-xs text-amber-700">{view.unconverted} amount(s) have no exchange rate and are left out of the converted totals.</p>
          )}
        </>
      )}
    </div>
  );
}

export default BROSTaxOverview;
